import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import GoogleAutocomplete from "react-google-autocomplete";
import { getGeocode, getLatLng } from "use-places-autocomplete";
import { setFilters } from "store/filters/operations";
import { getQueryParamsFromFilters } from "helpers/searchParams";
import styles from "./styles.module.scss";
import getCapacityOptions from "helpers/getCapacityOptions";
import { useTranslation } from "react-i18next";
import { Datepicker, FiltersDropdown, UsageIcon } from "reusableComponents";
import { getUsageOptions } from "services/filters.services";
import { useLanguage } from "context";
import { addDays, addHours, startOfDay } from "date-fns";

// Create a utility for lazy loading Google Maps
const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    // If already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    // If already loading
    if (document.querySelector('script[src*="maps.googleapis"]')) {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      return;
    }

    // Create callback function
    window.initGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
      } else {
        reject(new Error('Google Maps places library not loaded'));
      }
    };

    // Create script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
};

const SearchForm = ({ selectedUsage, onUsageChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [t] = useTranslation("global");
  const [isLoading, setIsLoading] = useState(false);
  
  // State for lazy loading
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [place, setPlace] = useState({ address: "", lat: null, lng: null });
  const [capacity, setCapacity] = useState(null);
  const [date, setDate] = useState(null);
  const [usageOptions, setUsageOptions] = useState([]);
  
  const capacityOptions = getCapacityOptions({ usages: [] }, t);
  const locationInputRef = useRef(null);

  // Load Google Maps when component mounts (but only if API key exists)
  useEffect(() => {
    if (!googleMapsApiKey) {
      setMapsError("Google Maps API key is missing");
      return;
    }

    let isMounted = true;

    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript(googleMapsApiKey);
        if (isMounted) {
          setMapsLoaded(true);
          console.log("Google Maps loaded successfully");
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to load Google Maps:", error);
          setMapsError(error.message);
        }
      }
    };

    loadMaps();

    return () => {
      isMounted = false;
    };
  }, [googleMapsApiKey]);

  // Handle focus on location input to show autocomplete
  const handleLocationFocus = () => {
    if (mapsLoaded) {
      setShowAutocomplete(true);
    }
  };

  useEffect(() => {
    const fetchUsageOptions = async () => {
      try {
        const usages = await getUsageOptions(language);
        const filteredUsages = usages
          // .filter(usage => usage.key !== "private_office")
          .map(usage => ({
            id: usage.id,
            name: usage.name,
            key: usage.key,
            activeIcon: usage.image_active_url,
            inactiveIcon: usage.image_inactive_url
          }));
        setUsageOptions(filteredUsages);
        
        // Set initial selected usage if not already set
        if (!selectedUsage) {
          const meetingRoom = filteredUsages.find(usage => usage.key === "mweeting_room");
          if (meetingRoom) {
            onUsageChange(meetingRoom);
          } else if (filteredUsages.length > 0) {
            onUsageChange(filteredUsages[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching usage options:", error);
        setUsageOptions([]);
      }
    };

    fetchUsageOptions();
  }, [language, selectedUsage, onUsageChange]);

  const handlePlaceSelect = async (place) => {
    try {
      const results = await getGeocode({ address: place.formatted_address });
      const { lat, lng } = await getLatLng(results[0]);
      setPlace({
        address: place.formatted_address,
        lat,
        lng,
      });
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const filters = {
        text: place.address,
        lat: place.lat,
        lng: place.lng,
        capacity,
        from: date ? date.toISOString().split("T")[0] : null,
        usages: selectedUsage ? [selectedUsage.key] : []
      };

      const queryParams = getQueryParamsFromFilters(filters);
      dispatch(setFilters(filters));
      navigate(`/search?${queryParams}`);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if API key is missing
  if (!googleMapsApiKey) {
    return (
      <div className={styles.searchFormContainer}>
        <div className={styles.errorMessage}>
          Google Maps API key is missing. Please check your .env file.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.searchFormContainer}>
      <form className={styles.searchForm} onSubmit={handleSubmit}>
        {/* Usage tabs section with icons */}
        <div className={styles.searchFormUsageTabs}>
          {usageOptions.map((usage) => (
            <button
              key={usage.id}
              type="button"
              className={`${styles.searchFormUsageTab} ${
                selectedUsage?.id === usage.id ? styles.searchFormUsageTabActive : ""
              }`}
              onClick={() => onUsageChange(usage)}
            >
              <UsageIcon
                fillColor="#eab413"
                size="34px"
                type={usage.key} 
                active={selectedUsage?.id === usage.id} 
              />
              <span className={styles.searchFormUsageTabText}>{usage.name}</span>
            </button>
          ))}
        </div>
        
        <div className={styles.searchFormRow}>
          {/* Location Input - with lazy loading */}
          <div className={styles.searchFormInputGroup}>
            <label className={styles.searchFormLabel}>{t("searchBar.placeQuestion")}</label>
            {!showAutocomplete ? (
              <input
                ref={locationInputRef}
                type="text"
                className={`${styles.searchFormInput} ${!mapsLoaded ? styles.loadingInput : ''}`}
                placeholder={
                  !mapsLoaded 
                    ? t("searchBar.loadingMaps") || "Loading maps..." 
                    : t("searchBar.searchPlaceholder")
                }
                onFocus={handleLocationFocus}
                disabled={!mapsLoaded}
                readOnly={!mapsLoaded}
              />
            ) : (
              <GoogleAutocomplete
                apiKey={googleMapsApiKey}
                onPlaceSelected={handlePlaceSelect}
                options={{ 
                  types: ["establishment", "geocode"],
                  fields: ["formatted_address", "geometry", "name", "place_id"]
                }}
                className={styles.searchFormInput}
                placeholder={t("searchBar.searchPlaceholder")}
                defaultValue={place.address}
              />
            )}
            {!mapsLoaded && !mapsError && (
              <div className={styles.loadingIndicator}>
                <span className={styles.spinner}></span>
              </div>
            )}
            {mapsError && (
              <div className={styles.errorIndicator}>
                ⚠️ Maps unavailable
              </div>
            )}
          </div>

          <div className={styles.searchFormInputGroup}>
            <label className={styles.searchFormLabel}>{t("searchBar.dateQuestion")}</label>
            <Datepicker
              styled={false}
              selectedDate={date}
              onDateChange={(date) =>
                setDate(date instanceof Date ? date : new Date(date))
              }
  minDate={addDays(startOfDay(new Date()), 2)} // Disables exactly today and tomorrow
              />
          </div>
          
          <div className={styles.searchFormInputGroup}>
            <label className={styles.searchFormLabel}>{t("searchBar.persQuestion")}</label>
            <FiltersDropdown
              styled={false}
              options={capacityOptions}
              selected={capacity}
              onSelect={setCapacity}
              placeholder={t("searchBar.persPlaceholder")}
            />
          </div>

          <button
            type="submit"
            className={`${styles.searchFormButton} ${isLoading ? styles.searchFormButtonLoading : ''}`}
            disabled={isLoading || !mapsLoaded}
          >
            <span className={styles.searchFormButtonIcon}></span>
            <span className={styles.searchFormButtonText}>{t("buttons.search")}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchForm;